import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PushSubscriptionRecord } from '@equip-track/shared';
import { ApiService } from './api.service';

export const PUSH_SERVICE_WORKER_URL = '/push-sw.js';

export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly apiService = inject(ApiService);

  isSupported(): boolean {
    return (
      typeof globalThis !== 'undefined' &&
      'Notification' in globalThis &&
      'serviceWorker' in navigator &&
      'PushManager' in globalThis
    );
  }

  getPermission(): BrowserNotificationPermission {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  async requestPermission(): Promise<BrowserNotificationPermission> {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.requestPermission();
  }

  async ensurePushSubscription(
    vapidPublicKey: string
  ): Promise<PushSubscriptionRecord | null> {
    if (!this.isSupported() || !vapidPublicKey) {
      return null;
    }

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    const registration = await this.registerServiceWorker();
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey),
      }));

    const record = this.toPushSubscriptionRecord(subscription);
    await firstValueFrom(
      this.apiService.endpoints.subscribePush.execute({ subscription: record })
    );
    return record;
  }

  async removePushSubscription(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration(
      PUSH_SERVICE_WORKER_URL
    );
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await firstValueFrom(
      this.apiService.endpoints.unsubscribePush.execute({ endpoint })
    );
  }

  async syncIfEnabled(
    enabled: boolean,
    browserChannel: boolean,
    vapidPublicKey: string | null
  ): Promise<boolean> {
    if (!enabled || !browserChannel || !vapidPublicKey) {
      await this.removePushSubscription().catch(() => undefined);
      return false;
    }

    const record = await this.ensurePushSubscription(vapidPublicKey);
    return !!record;
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration(
      PUSH_SERVICE_WORKER_URL
    );
    if (existing) {
      // Roll out service worker updates (e.g. the click beacon) promptly instead
      // of waiting for the browser's own periodic update check.
      existing.update().catch(() => undefined);
      return existing;
    }
    await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_URL, {
      scope: '/',
    });
    // PushManager.subscribe rejects while the freshly registered worker is
    // still installing; ready resolves once it is active.
    return navigator.serviceWorker.ready;
  }

  private toPushSubscriptionRecord(
    subscription: PushSubscription
  ): PushSubscriptionRecord {
    const json = subscription.toJSON();
    if (
      !json.endpoint ||
      !json.keys?.['p256dh'] ||
      !json.keys?.['auth']
    ) {
      throw new Error('Invalid push subscription');
    }
    return {
      endpoint: json.endpoint,
      expirationTime: json.expirationTime ?? null,
      keys: {
        p256dh: json.keys['p256dh'],
        auth: json.keys['auth'],
      },
    };
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
